import { isArray } from 'lodash';
import {
  AsyncResp,
  FailedResp,
  fetchAll,
  Resp,
} from '@paradigm/api/src/fetch-api';
import { logException } from '@paradigm/logging/src/logging';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '#/unified-rfqs/store/trade-tape';

import RfqsStore from '#/unified-rfqs/store/rfqs';
import { DrfqOrderbooksStore } from '#/unified-rfqs/store/drfq-orderbooks';
import * as ApiRepository from '#/unified-rfqs/repositories/drfq-api';

import OrdersStore from '#/unified-rfqs//store/orders';
import { DrfqOrdersStore } from '#/unified-rfqs/store/drfq-orders';
import {
  CreatedPricedOrderReq,
  CreateRfqReq,
  PricingResp,
  RfqIdReq,
} from '#/unified-rfqs/repositories/drfq-types';
import { DrfqHistoryStore } from '#/unified-rfqs/store/drfq-history';
import { DrfqDraftsStore } from '#/unified-rfqs/store/drfq-drafts';
import { Order } from '#/unified-rfqs/entities/drfq/orders';
import { createFailedResp } from '#/unified-rfqs/services/shared';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import { DrfqServiceRequestError } from '@paradigm/logging/src/errors';
import { findBaseProductCode } from '#/products/domain';
import type { Breadcrumb } from '@sentry/types';

interface DrfqServiceDeps {
  api: typeof ApiRepository;
  rfqStore: RfqsStore;
  tradesStore: TradesStore;
  tradeTapeStore: TradeTapeStore;
  ordersStore: OrdersStore;
  orderbooksStore: DrfqOrderbooksStore;
  drfqOrdersStore: DrfqOrdersStore;
  drfqHistoryStore: DrfqHistoryStore;
  drfqDraftsStore: DrfqDraftsStore;
}

type PricingMismatchResp = PricingResp & { isPriceMismatch: true };

export interface DrfqService {
  readonly createRfq: typeof ApiRepository.createRfq;
  readonly repriceRfq: (
    rfqId: Drfq['id'],
  ) => ReturnType<typeof ApiRepository.createRfq>;
  readonly listInstruments: typeof ApiRepository.listInstruments;
  readonly listActiveDrfqs: typeof ApiRepository.listRfqs;
  readonly listDrfqHistory: typeof ApiRepository.listRfqs;
  readonly listDrfqDrafts: typeof ApiRepository.listRfqs;
  readonly listOrders: typeof ApiRepository.listOrders;
  readonly listTrades: typeof ApiRepository.listTrades;
  readonly listTradeTape: typeof ApiRepository.listTradeTape;
  readonly cancelOrder: typeof ApiRepository.cancelOrder;
  readonly cancelOrdersBatch: typeof ApiRepository.cancelAllOrders;
  readonly getRfqOrderbook: typeof ApiRepository.getRfqOrders;
  readonly getRfqOrders: (
    req: RfqIdReq,
  ) => ReturnType<typeof ApiRepository.listOrders>;
  readonly getDrfq: typeof ApiRepository.getRfq;
  readonly cancelDrfq: typeof ApiRepository.cancelRfq;
  readonly createPricedOrder: (
    req: CreatedPricedOrderReq,
  ) => AsyncResp<Resp<Order>[] | PricingMismatchResp>;
  readonly createOrder: typeof ApiRepository.createOrder;
  readonly replaceOrder: typeof ApiRepository.replaceOrder;
  readonly createOrReplaceOrder: typeof ApiRepository.createOrReplaceOrder;
}

class DrfqServiceImpl implements DrfqService {
  constructor(private deps: DrfqServiceDeps) {}

  createRfq = async (req: CreateRfqReq) => {
    const { api, rfqStore, drfqDraftsStore } = this.deps;
    const resp = await api.createRfq(req);

    return handleResp(resp, (drfq) => {
      if (req.state === 'DRAFT') {
        drfqDraftsStore.upsertMany([drfq]);
      } else {
        rfqStore.upsertMany([drfq]);
      }
    });
  };

  repriceRfq = async (rfqId: Drfq['id']) => {
    const { rfqStore } = this.deps;
    const existingRfq = rfqStore.selectById(rfqId);

    if (existingRfq == null || existingRfq.type !== 'drfq') {
      return {
        ...getEmptyResp(),
        ok: false,
        message: 'Rfq is not available.',
        data: {},
      } as const;
    }

    // Explicitly set state to OPEN. The base RFQ
    // might be in state RECENTLY_CLOSED when re-priced
    const createRfqReq: CreateRfqReq = {
      ...existingRfq,
      state: 'OPEN',
    };

    const resp = await this.createRfq(createRfqReq);

    return handleResp(resp, () =>
      rfqStore.updateOne(rfqId, { state: 'CLOSED' }),
    );
  };

  listInstruments: typeof ApiRepository.listInstruments = async (req) => {
    const { api } = this.deps;
    const resp = await fetchAll(
      { ...req, includeGreeks: true },
      api.listInstruments,
    );
    return handleResp(resp);
  };

  listActiveDrfqs: typeof ApiRepository.listRfqs = async (req) => {
    const { api, rfqStore } = this.deps;
    const resp = await api.listRfqs({ ...req, state: 'OPEN' });

    return handleResp(resp, (data) => rfqStore.addMany(data.results));
  };

  listDrfqHistory: typeof ApiRepository.listRfqs = async (req) => {
    const { api, drfqHistoryStore } = this.deps;
    const resp = await api.listRfqs({ ...req, state: 'CLOSED' });

    return handleResp(resp, (data) => drfqHistoryStore.addMany(data.results));
  };

  listDrfqDrafts: typeof ApiRepository.listRfqs = async (req) => {
    const { api, drfqDraftsStore } = this.deps;
    const resp = await api.listRfqs({ ...req, state: 'DRAFT' });

    return handleResp(resp, (data) => drfqDraftsStore.addMany(data.results));
  };

  listOrders: typeof ApiRepository.listOrders = async (req) => {
    const { api, ordersStore } = this.deps;
    const resp = await api.listOrders(req);

    return handleResp(resp, (data) => ordersStore.addMany(data.results));
  };

  listTrades: typeof ApiRepository.listTrades = async (req) => {
    const { api, tradesStore } = this.deps;
    const resp = await api.listTrades(req);

    return handleResp(resp, (data) => tradesStore.addMany(data.results));
  };

  listTradeTape: typeof ApiRepository.listTradeTape = async (req) => {
    const { api, tradeTapeStore } = this.deps;
    const resp = await api.listTradeTape(req);

    return handleResp(resp, (data) => tradeTapeStore.addMany(data.results));
  };

  cancelOrder: typeof ApiRepository.cancelOrder = async (req) => {
    const { api, ordersStore } = this.deps;
    const { orderId } = req;
    const order = ordersStore.selectById(orderId);

    if (order == null) return createFailedResp('Order not found locally');
    if (order.rfqType !== 'drfq') return createFailedResp('Invalid order type');

    const resp = await api.cancelOrder(req);

    // Order removal is handled by WS
    return handleResp(resp);
  };

  cancelOrdersBatch: typeof ApiRepository.cancelAllOrders = async (req) => {
    const { api } = this.deps;
    const resp = await api.cancelAllOrders(req);

    return handleResp(resp);
  };

  getRfqOrderbook: typeof ApiRepository.getRfqOrders = async (req) => {
    const { api, orderbooksStore } = this.deps;
    const resp = await api.getRfqOrders(req);

    return handleResp(resp, (data) => {
      orderbooksStore.byRfq(req.rfqId).bySide('BUY').addMany(data.bids);
      orderbooksStore.byRfq(req.rfqId).bySide('SELL').addMany(data.asks);
    });
  };

  getRfqOrders = async ({ rfqId }: RfqIdReq) => {
    const { api, drfqOrdersStore, ordersStore } = this.deps;
    const resp = await api.listOrders({ rfqId });

    return handleResp(resp, (data) => {
      drfqOrdersStore.byRfq(rfqId).addMany(data.results);
      ordersStore.addMany(data.results);
    });
  };

  getDrfq: typeof ApiRepository.getRfq = async (req: RfqIdReq) => {
    const { api, rfqStore, drfqDraftsStore } = this.deps;
    const resp = await api.getRfq(req);

    return handleResp(resp, (data) => {
      if (data.state === 'DRAFT') {
        drfqDraftsStore.upsertMany([data]);
      } else {
        rfqStore.upsertMany([data]);
      }
    });
  };

  cancelDrfq: typeof ApiRepository.cancelRfq = async (req) => {
    const { api } = this.deps;
    const resp = await api.cancelRfq(req);
    return handleResp(resp);
  };

  createOrder: typeof ApiRepository.createOrder = async (req) => {
    const { api } = this.deps;
    const resp = await api.createOrder(req);
    return handleResp(resp, undefined, true);
  };

  replaceOrder: typeof ApiRepository.replaceOrder = async (req) => {
    const { api } = this.deps;
    const resp = await api.replaceOrder(req);
    return handleResp(resp, undefined, true);
  };

  createOrReplaceOrder: typeof ApiRepository.createOrReplaceOrder = async (
    req,
  ) => {
    const { api } = this.deps;
    const resp = await api.createOrReplaceOrder(req);
    return handleResp(resp, undefined, true);
  };

  createPricedOrder: DrfqService['createPricedOrder'] = async (req) => {
    const { api } = this.deps;
    const {
      rfqId,
      quantity,
      accountName,
      productCodes,
      shouldUseFallbackAlgo,
      bid_id,
      ask_id,
      body: _body,
      signal: _signal,
      algorithm_version,
      bid_price,
      ask_price,
    } = req;

    const productCode = findBaseProductCode(productCodes);
    const pricingResp = await api.getPricing({
      accept_estimated_prices: shouldUseFallbackAlgo,
      bid_price,
      ask_price,
      legs: req.legs.filter((leg) => !leg.isHedge),
      algorithm_version,
    });

    if (!pricingResp.ok) {
      return handleResp(pricingResp) as FailedResp;
    }

    const isBidMismatch =
      bid_price != null && pricingResp.data.total_bid_price !== bid_price;
    const isAskMismatch =
      ask_price != null && pricingResp.data.total_ask_price !== ask_price;
    if (isBidMismatch || isAskMismatch) {
      return handleResp({
        ...pricingResp,
        data: { ...pricingResp.data, isPriceMismatch: true },
      });
    }

    const { ask_prices, bid_prices } = pricingResp.data;
    const getLegs = (pricingLegs: readonly string[]) => {
      if (pricingLegs.length === 0) return [];

      return req.legs.map((leg, index) => ({
        instrument_id: leg.instrument_id,
        ratio: leg.ratio,
        quantity,
        price: leg.isHedge ? leg.price! : pricingLegs[index]!,
      }));
    };
    const askLegs = getLegs(ask_prices);
    const bidLegs = getLegs(bid_prices);

    if (
      !arePricingLegsValid(productCode, quantity, bidLegs) &&
      !arePricingLegsValid(productCode, quantity, askLegs)
    ) {
      return handleResp(getPrincingInvalidMgpResp()) as FailedResp;
    }

    const baseArgs = {
      rfq_id: rfqId,
      account_name: accountName,
      type: 'LIMIT',
      time_in_force: 'GOOD_TILL_CANCELED',
      quantity,
    } as const;

    const promises = [];
    if (bidLegs.length > 0) {
      promises.push(
        this.createOrReplaceOrder({
          orderId: bid_id,
          order: { ...baseArgs, side: 'BUY', legs: bidLegs },
        }),
      );
    }

    if (askLegs.length > 0) {
      promises.push(
        this.createOrReplaceOrder({
          orderId: ask_id,
          order: { ...baseArgs, side: 'SELL', legs: askLegs },
        }),
      );
    }

    return { ...getEmptyResp(), data: await Promise.all(promises) };
  };
}

const handleResp = <T>(
  resp: Resp<T>,
  save?: (data: T) => void,
  handleValidationErrors = false,
) => {
  if (resp.ok) {
    save?.(resp.data);
    return resp;
  }

  logException(...processDrfqException(resp));

  return handleValidationErrors ? parseValidationErrors(resp) : resp;
};

export function parseValidationErrors(resp: FailedResp): FailedResp {
  const { data, message } = resp;
  const validationErrors = Object.values(data.data ?? {});
  const errorData = validationErrors[0];

  if (isArray(errorData)) {
    return { ...resp, message: errorData[0]?.toString() ?? message };
  }

  if (typeof errorData === 'string') {
    return { ...resp, message: errorData };
  }

  return resp;
}

export function processDrfqException(
  resp: FailedResp,
): [Breadcrumb, DrfqServiceRequestError] {
  const { data, message, status } = resp;
  const drfqRequestError = new DrfqServiceRequestError(message, status);
  return [data, drfqRequestError];
}

function getEmptyResp() {
  return {
    ok: true,
    error: '',
    message: '',
    status: 200,
    data: null,
  } as const;
}

function getPrincingInvalidMgpResp() {
  return {
    ...getEmptyResp(),
    ok: false,
    data: {},
    message:
      'Cannot calculate sensical leg prices with valid Minium Gross Premium.',
  } as const;
}

export function createDrfqService({
  orderbooksStore,
  drfqOrdersStore,
  drfqHistoryStore,
  drfqDraftsStore,
  tradeTapeStore,
  rfqStore,
  ordersStore,
  tradesStore,
}: Omit<DrfqServiceDeps, 'api'>): DrfqService {
  // deps
  const api = ApiRepository;

  return new DrfqServiceImpl({
    api,
    rfqStore,
    tradesStore,
    tradeTapeStore,
    ordersStore,
    drfqOrdersStore,
    orderbooksStore,
    drfqHistoryStore,
    drfqDraftsStore,
  });
}

type ValidationLeg = {
  instrument_id: number;
  ratio: string;
  quantity: string;
  price: string;
};

function arePricingLegsValid(
  _productCode: string | undefined,
  _quantity: string,
  _legs: ValidationLeg[],
) {
  // Always return true since MGP validation has been removed
  return true;
}
